import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { useThemeMode } from '../../theme/ThemeProvider';
import logo from '../../Images/Logo/symbian-logo.svg';

function SignOutOverlay() {
  const { isDark } = useThemeMode();

  const c = {
    bg: isDark ? '#050C1F' : '#F4F7FF',
    cardBg: isDark ? '#0C1A2E' : '#FFFFFF',
    cardShadow: isDark
      ? '0 0 0 1px rgba(255,255,255,0.06), 0 40px 100px rgba(0,0,0,0.80), 0 0 90px -10px rgba(3,93,184,0.24)'
      : '0 0 0 1px rgba(3,93,184,0.07), 0 28px 72px rgba(3,93,184,0.12), 0 4px 20px rgba(0,0,0,0.05)',
    shimmer: isDark
      ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.10), transparent)'
      : 'linear-gradient(90deg, transparent, rgba(3,93,184,0.13), transparent)',
    aura: isDark
      ? 'radial-gradient(circle, rgba(3,93,184,0.18) 0%, rgba(3,93,184,0.05) 45%, transparent 68%)'
      : 'radial-gradient(circle, rgba(3,93,184,0.10) 0%, rgba(3,93,184,0.03) 50%, transparent 70%)',
    outerTrack: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(3,93,184,0.09)',
    outerArc1: isDark ? '#2B8DE0' : '#035DB8',
    outerArc2: isDark ? 'rgba(43,141,224,0.28)' : 'rgba(3,93,184,0.26)',
    innerTrack: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(3,93,184,0.07)',
    innerArc1: isDark ? 'rgba(94,175,240,0.78)' : '#5EAFF0',
    innerArc2: isDark ? 'rgba(94,175,240,0.28)' : 'rgba(94,175,240,0.42)',
    shieldColor: isDark ? '#5EAFF0' : '#035DB8',
    heading: isDark ? '#FFFFFF' : '#013976',
    subtitle: isDark ? 'rgba(148,163,184,0.85)' : 'rgba(100,116,139,0.88)',
    barTrack: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(3,93,184,0.08)',
    barFill: isDark
      ? 'linear-gradient(90deg, #013976, #035DB8, #5EAFF0)'
      : 'linear-gradient(90deg, #024D9A, #035DB8, #5EAFF0)',
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-0 z-[99999] flex items-center justify-center
                 select-none pointer-events-all overflow-hidden"
      style={{ background: c.bg }}
    >
      <div className="absolute top-0 left-0 right-0 h-[3px] overflow-hidden z-20">
        <motion.div
          className="h-full rounded-r-full"
          style={{ width: '48%', background: c.barFill }}
          initial={{ x: '-115%' }}
          animate={{ x: '270%' }}
          transition={{ duration: 1.7, ease: [0.4, 0, 0.6, 1], repeat: Infinity, repeatDelay: 0.1 }}
        />
      </div>

      <div
        className="absolute pointer-events-none
                   w-[560px] h-[560px]
                   sm:w-[680px] sm:h-[680px]
                   lg:w-[820px] lg:h-[820px]
                   xl:w-[960px] xl:h-[960px]"
        style={{ borderRadius: '50%', background: c.aura, filter: 'blur(36px)' }}
      />

      {/* ── Card ── */}
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1], delay: 0.06 }}
        className="relative flex flex-col items-center overflow-hidden
                   rounded-[20px] sm:rounded-[22px] lg:rounded-[26px]
                   w-[320px] sm:w-[380px] md:w-[420px] lg:w-[460px] xl:w-[480px]
                   px-8 py-10
                   sm:px-10 sm:py-11
                   md:px-12 md:py-12
                   lg:px-14 lg:py-13
                   xl:px-16 xl:py-14"
        style={{ background: c.cardBg, boxShadow: c.cardShadow }}
      >
        <div
          className="absolute top-0 left-[12%] right-[12%] h-px rounded-full pointer-events-none"
          style={{ background: c.shimmer }}
        />
        <motion.img
          src={logo}
          alt="Symbian Health"
          draggable={false}
          className="object-contain mb-8 sm:mb-9 lg:mb-10
                     h-[22px] sm:h-[24px] md:h-[26px] lg:h-[28px] xl:h-[30px]"
          style={{ opacity: isDark ? 0.90 : 0.85, filter: isDark ? 'brightness(0) invert(1)' : 'none' }}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: isDark ? 0.90 : 0.85, y: 0 }}
          transition={{ delay: 0.14, duration: 0.32 }}
        />

        <motion.div
          className="relative flex-shrink-0 mb-8 sm:mb-9 lg:mb-10
                     w-[72px] h-[72px]
                     sm:w-[80px] sm:h-[80px]
                     md:w-[88px] md:h-[88px]
                     lg:w-[96px] lg:h-[96px]
                     xl:w-[100px] xl:h-[100px]"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.18, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            className="absolute inset-0 rounded-full"
            style={{ border: `4px solid ${c.outerTrack}` }}
          />
          <div
            className="absolute inset-0 rounded-full"
            style={{
              border: '4px solid transparent',
              borderTopColor: c.outerArc1,
              borderRightColor: c.outerArc2,
              animation: 'spin 1.1s linear infinite',
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              inset: 13,
              border: `3px solid ${c.innerTrack}`,
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              inset: 13,
              border: '3px solid transparent',
              borderTopColor: c.innerArc1,
              borderLeftColor: c.innerArc2,
              animation: 'spin 0.78s linear infinite reverse',
            }}
          />
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            animate={{ scale: [1, 1.12, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ShieldCheck
              className="w-[18px] h-[18px] sm:w-[20px] sm:h-[20px] md:w-[22px] md:h-[22px] lg:w-[24px] lg:h-[24px]"
              strokeWidth={1.75}
              color={c.shieldColor}
            />
          </motion.div>
        </motion.div>

        <motion.div
          className="flex flex-col items-center text-center mb-8 sm:mb-9 lg:mb-10"
          style={{ gap: 10 }}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.22, duration: 0.32 }}
        >
          <h2
            className="font-bold tracking-tight m-0"
            style={{
              fontSize: 'clamp(16px, 1.4vw, 20px)',
              letterSpacing: '-0.02em',
              color: c.heading,
            }}
          >
            Signing out securely
          </h2>
          <p
            className="font-medium m-0"
            style={{
              fontSize: 'clamp(12px, 0.95vw, 14px)',
              lineHeight: 1.6,
              maxWidth: 210,
              color: c.subtitle,
            }}
          >
            Protecting your health information
          </p>
        </motion.div>
        <motion.div
          className="w-full rounded-full overflow-hidden"
          style={{ height: 3, background: c.barTrack }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.28 }}
        >
          <motion.div
            className="h-full rounded-full"
            style={{ width: '52%', background: c.barFill }}
            initial={{ x: '-115%' }}
            animate={{ x: '220%' }}
            transition={{ duration: 1.9, ease: [0.4, 0, 0.6, 1], repeat: Infinity, repeatDelay: 0.08 }}
          />
        </motion.div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}

export default SignOutOverlay;
